// src/components/SensorPage/Insights.jsx - Design Ultra Premium com IA
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiAlertTriangle, 
  FiCheckCircle, 
  FiInfo, 
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
  FiZap,
  FiTarget,
  FiCpu,
  FiLayers,
  FiFileText, 
  FiAlertCircle,
  FiBell,
  FiMessageSquare,
  FiBookOpen,
  FiAward,
  FiShield,
  FiThermometer,
  FiWind,
  FiDroplet,
  FiClock,
  FiCalendar,
  FiBarChart2,
  FiPieChart,
  FiRefreshCw,
  FiEye,
  FiFilter,
  FiSearch,
  FiStar,
  FiHash,
  FiCode,
  FiTool,
  FiChevronDown,
  FiChevronUp
} from 'react-icons/fi';
import { 
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';
import './InsightsPage.css';

const Insights = ({ data, activeSubsection }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTimeframe, setSelectedTimeframe] = useState('7d');
  const [expandedInsight, setExpandedInsight] = useState(null);
  const [aiProcessing, setAiProcessing] = useState(false);

  // Simular processamento de IA
  useEffect(() => {
    setAiProcessing(true);
    const timer = setTimeout(() => setAiProcessing(false), 1500);
    return () => clearTimeout(timer);
  }, [activeSubsection]);

  // Insights de IA gerados dinamicamente
  const generateAIInsights = () => {
    const insights = [
      {
        id: 1,
        type: 'optimization',
        priority: 'high',
        category: 'energy',
        icon: FiZap,
        title: 'Oportunidade de Otimização Energética Detectada',
        summary: 'Sistema identificou padrão de desperdício energético durante períodos de baixa ocupação',
        description: 'A análise de machine learning identificou que o sistema HVAC mantém operação em capacidade total mesmo durante períodos com ocupação inferior a 20%. Implementar controle adaptativo baseado em ocupação real.',
        metrics: {
          potential: 'Redução de 25% no consumo',
          confidence: 92,
          complexity: 'Média',
          timeToImplement: '2-3 semanas'
        },
        recommendations: [
          'Implementar sensores de ocupação em tempo real',
          'Ajustar algoritmo de controle para modo econômico automático',
          'Configurar setback temperature para períodos de baixa ocupação'
        ],
        relatedData: {
          occupancyPattern: [
            { time: '06h', occupation: 5, hvac: 100 },
            { time: '08h', occupation: 30, hvac: 100 },
            { time: '10h', occupation: 85, hvac: 100 },
            { time: '12h', occupation: 95, hvac: 100 },
            { time: '14h', occupation: 80, hvac: 100 },
            { time: '16h', occupation: 60, hvac: 100 },
            { time: '18h', occupation: 20, hvac: 100 },
            { time: '20h', occupation: 5, hvac: 80 }
          ]
        },
        timestamp: new Date().toISOString(),
        aiModel: 'EnergyOptimizer v3.2'
      },
      {
        id: 2,
        type: 'anomaly',
        priority: 'medium',
        category: 'comfort',
        icon: FiAlertTriangle,
        title: 'Anomalia no Padrão de Conforto Térmico',
        summary: 'Desvio significativo detectado no índice PMV durante horário de pico',
        description: 'O modelo preditivo identificou que o PMV está consistentemente fora da zona de conforto ideal (-0.5 a +0.5) entre 14h e 16h nos últimos 5 dias úteis, coincidindo com maior incidência solar na fachada oeste.',
        metrics: {
          deviation: '+0.8 PMV',
          affectedPeriod: '14h-16h',
          frequency: '5 dias consecutivos',
          impactedUsers: '~45 pessoas'
        },
        recommendations: [
          'Ajustar setpoint em -1.5°C durante período crítico',
          'Avaliar instalação de películas de controle solar',
          'Redistribuir carga térmica com ventilação direcionada'
        ],
        relatedData: {
          pmvTrend: [
            { day: 'Seg', pmv14h: 0.7, pmv16h: 0.8 },
            { day: 'Ter', pmv14h: 0.75, pmv16h: 0.85 },
            { day: 'Qua', pmv14h: 0.8, pmv16h: 0.9 },
            { day: 'Qui', pmv14h: 0.72, pmv16h: 0.82 },
            { day: 'Sex', pmv14h: 0.78, pmv16h: 0.88 }
          ]
        },
        timestamp: new Date().toISOString(),
        aiModel: 'ComfortAnalyzer v2.8'
      },
      {
        id: 3,
        type: 'prediction',
        priority: 'low',
        category: 'maintenance',
        icon: FiTrendingUp,
        title: 'Previsão de Manutenção Preventiva',
        summary: 'Degradação gradual na eficiência do sistema de filtragem detectada',
        description: 'Algoritmo de análise preditiva identificou aumento progressivo de 15% nas partículas PM2.5 internas apesar de condições externas estáveis. Padrão sugere saturação dos filtros HEPA.',
        metrics: {
          degradation: '15% em 30 dias',
          currentEfficiency: '82%',
          predictedFailure: '45-60 dias',
          reliability: '88%'
        },
        recommendations: [
          'Agendar inspeção dos filtros na próxima semana',
          'Verificar pressão diferencial no sistema',
          'Preparar substituição preventiva de filtros'
        ],
        relatedData: {
          filterEfficiency: [
            { week: 'S1', efficiency: 95, pm25: 8 },
            { week: 'S2', efficiency: 92, pm25: 10 },
            { week: 'S3', efficiency: 88, pm25: 12 },
            { week: 'S4', efficiency: 82, pm25: 15 }
          ]
        },
        timestamp: new Date().toISOString(),
        aiModel: 'PredictiveMaintenance v4.1'
      },
      {
        id: 4,
        type: 'pattern',
        priority: 'medium',
        category: 'productivity',
        icon: FiActivity,
        title: 'Correlação Identificada: CO₂ vs Produtividade',
        summary: 'IA detectou impacto direto dos níveis de CO₂ em métricas de desempenho',
        description: 'Análise de correlação cruzada revelou que aumentos de CO₂ acima de 800ppm coincidem com redução de 12% na velocidade de resposta em sistemas internos e aumento de 8% em erros reportados.',
        metrics: {
          correlation: '0.76 (forte)',
          co2Threshold: '800 ppm',
          productivityImpact: '-12%',
          optimalRange: '400-600 ppm'
        },
        recommendations: [
          'Aumentar taxa de renovação de ar quando CO₂ > 700ppm',
          'Implementar alertas proativos para gestores',
          'Considerar sistema de ventilação sob demanda (DCV)'
        ],
        relatedData: {
          correlation: [
            { co2: 400, productivity: 100 },
            { co2: 500, productivity: 98 },
            { co2: 600, productivity: 95 },
            { co2: 700, productivity: 92 },
            { co2: 800, productivity: 88 },
            { co2: 900, productivity: 85 },
            { co2: 1000, productivity: 82 }
          ]
        },
        timestamp: new Date().toISOString(),
        aiModel: 'ProductivityOptimizer v1.9'
      },
      {
        id: 5,
        type: 'success',
        priority: 'info',
        category: 'achievement',
        icon: FiAward,
        title: 'Meta de Qualidade do Ar Alcançada',
        summary: 'Ambiente manteve IAQ > 85 por 7 dias consecutivos',
        description: 'Sistema registrou conquista importante: Índice de Qualidade do Ar Interior mantido acima de 85/100 durante toda a semana, superando meta estabelecida de 80/100.',
        metrics: {
          averageIAQ: 87.3,
          peakIAQ: 92,
          consistency: '98%',
          benchmark: 'Top 10% do setor'
        },
        recommendations: [
          'Documentar configurações atuais como baseline',
          'Compartilhar best practices com outras unidades',
          'Manter monitoramento para garantir consistência'
        ],
        relatedData: {
          weeklyIAQ: [
            { day: 'Seg', iaq: 86 },
            { day: 'Ter', iaq: 88 },
            { day: 'Qua', iaq: 87 },
            { day: 'Qui', iaq: 89 },
            { day: 'Sex', iaq: 85 },
            { day: 'Sáb', iaq: 88 },
            { day: 'Dom', iaq: 90 }
          ]
        },
        timestamp: new Date().toISOString(),
        aiModel: 'QualityMonitor v3.5'
      }
    ];

    return insights;
  };

  const aiInsights = generateAIInsights();

  // Componente para Sugestões IA
  const AISuggestionsView = () => {
    const categories = [
      { id: 'all', label: 'Todos', count: aiInsights.length },
      { id: 'energy', label: 'Energia', count: 1, icon: FiZap },
      { id: 'comfort', label: 'Conforto', count: 1, icon: FiThermometer },
      { id: 'maintenance', label: 'Manutenção', count: 1, icon: FiTool },
      { id: 'productivity', label: 'Produtividade', count: 1, icon: FiActivity },
      { id: 'achievement', label: 'Conquistas', count: 1, icon: FiAward }
    ];

    const filteredInsights = selectedCategory === 'all' 
      ? aiInsights 
      : aiInsights.filter(i => i.category === selectedCategory);

    const getPriorityColor = (priority) => {
      switch(priority) {
        case 'high': return '#ef4444';
        case 'medium': return '#f59e0b';
        case 'low': return '#3b82f6';
        case 'info': return '#10b981';
        default: return '#6b7280';
      }
    };

    return (
      <div className="insights-view-container">
        {/* Header Premium */}
        <div className="insights-header-premium ai-gradient">
          <div className="header-particles"></div>
          <div className="header-content-insights">
            <div className="header-left-insights">
              <motion.div 
                className="header-icon-insights"
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <FiCpu size={40} />
              </motion.div>
    <div>
                <h1 className="header-title-insights">Inteligência Artificial Analítica</h1>
                <p className="header-subtitle-insights">
                  Insights gerados por machine learning com análise preditiva avançada
                </p>
              </div>
            </div>
            <div className="header-right-insights">
              <div className="ai-status">
                <div className={`ai-indicator ${aiProcessing ? 'processing' : 'ready'}`}>
                  <div className="ai-dot"></div>
            </div>
                <span className="ai-status-text">
                  {aiProcessing ? 'Processando...' : 'IA Ativa'}
                </span>
            </div>
          </div>
        </div>
      </div>

        {/* Filters Bar */}
        <div className="filters-bar">
          <div className="category-filters">
            {categories.map(cat => (
              <motion.button
                key={cat.id}
                className={`filter-btn ${selectedCategory === cat.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {cat.icon && <cat.icon size={16} />}
                <span>{cat.label}</span>
                <span className="filter-count">{cat.count}</span>
              </motion.button>
            ))}
          </div>
          
          <div className="timeframe-selector">
            {['24h', '7d', '30d', '90d'].map(time => (
              <button
                key={time}
                className={`time-btn ${selectedTimeframe === time ? 'active' : ''}`}
                onClick={() => setSelectedTimeframe(time)}
              >
                {time}
              </button>
            ))}
          </div>
        </div>

        {/* AI Insights Grid */}
        <div className="insights-grid">
          <AnimatePresence>
            {filteredInsights.map((insight, index) => {
              const IconComponent = insight.icon;
              const isExpanded = expandedInsight === insight.id;
              
          return (
            <motion.div
                  key={insight.id}
                  className={`insight-card-ai priority-${insight.priority}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.1 }}
                  layout
                >
                  <div className="insight-header-ai">
                    <div className="insight-icon-wrapper" style={{ 
                      background: `${getPriorityColor(insight.priority)}15`,
                      color: getPriorityColor(insight.priority)
                    }}>
                      <IconComponent size={24} />
                </div>
                    <div className="insight-meta">
                      <span className="insight-category">{insight.category.toUpperCase()}</span>
                  <span className="insight-time">
                        {new Date(insight.timestamp).toLocaleTimeString('pt-BR', { 
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                    <div className={`priority-badge ${insight.priority}`}>
                      {insight.priority === 'high' ? 'Alta' : 
                       insight.priority === 'medium' ? 'Média' : 
                       insight.priority === 'low' ? 'Baixa' : 'Info'}
                    </div>
                  </div>

                  <div className="insight-body">
                    <h3 className="insight-title-ai">{insight.title}</h3>
                    <p className="insight-summary">{insight.summary}</p>
                    
                    {!isExpanded && (
                      <motion.button
                        className="expand-btn"
                        onClick={() => setExpandedInsight(insight.id)}
                        whileHover={{ scale: 1.05 }}
                      >
                        Ver análise completa
                        <FiChevronDown />
                      </motion.button>
                    )}

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          className="insight-expanded"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="expanded-content">
                            <p className="insight-description">{insight.description}</p>
                            
                            {/* Metrics Display */}
                            <div className="insight-metrics">
                              {Object.entries(insight.metrics).map(([key, value]) => (
                                <div key={key} className="metric-item">
                                  <span className="metric-label">
                                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}:
                                  </span>
                                  <span className="metric-value">{value}</span>
                                </div>
                              ))}
                            </div>

                            {/* Chart Visualization */}
                            {insight.relatedData && (
                              <div className="insight-chart">
                                <ResponsiveContainer width="100%" height={200}>
                                  {insight.type === 'optimization' ? (
                                    <AreaChart data={insight.relatedData.occupancyPattern}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                      <XAxis dataKey="time" stroke="#9ca3af" />
                                      <YAxis stroke="#9ca3af" />
                                      <Tooltip />
                                      <Area type="monotone" dataKey="occupation" stroke="#10b981" fill="#10b981" fillOpacity={0.3} name="Ocupação (%)" />
                                      <Area type="monotone" dataKey="hvac" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} name="HVAC (%)" />
                                    </AreaChart>
                                  ) : insight.type === 'anomaly' ? (
                                    <BarChart data={insight.relatedData.pmvTrend}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                      <XAxis dataKey="day" stroke="#9ca3af" />
                                      <YAxis stroke="#9ca3af" />
                                      <Tooltip />
                                      <Bar dataKey="pmv14h" fill="#f59e0b" name="PMV 14h" />
                                      <Bar dataKey="pmv16h" fill="#ef4444" name="PMV 16h" />
                                    </BarChart>
                                  ) : insight.type === 'pattern' ? (
                                    <LineChart data={insight.relatedData.correlation}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                      <XAxis dataKey="co2" stroke="#9ca3af" />
                                      <YAxis stroke="#9ca3af" />
                                      <Tooltip />
                                      <Line type="monotone" dataKey="productivity" stroke="#3b82f6" strokeWidth={3} dot={{ fill: '#3b82f6' }} />
                                    </LineChart>
                                  ) : (
                                    <LineChart data={Object.values(insight.relatedData)[0]}>
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                      <XAxis dataKey={Object.keys(Object.values(insight.relatedData)[0][0])[0]} stroke="#9ca3af" />
                                      <YAxis stroke="#9ca3af" />
                                      <Tooltip />
                                      <Line type="monotone" dataKey={Object.keys(Object.values(insight.relatedData)[0][0])[1]} stroke="#10b981" strokeWidth={2} />
                                    </LineChart>
                                  )}
                                </ResponsiveContainer>
                              </div>
                            )}

                            {/* Recommendations */}
                            <div className="insight-recommendations">
                              <h4>Recomendações da IA:</h4>
                              <ul>
                                {insight.recommendations.map((rec, i) => (
                                  <li key={i}>
                                    <FiCheckCircle />
                                    <span>{rec}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* AI Model Info */}
                            <div className="ai-model-info">
                              <FiCode size={14} />
                              <span>Modelo: {insight.aiModel}</span>
                              <span>•</span>
                              <span>Confiança: {insight.metrics.confidence || '95'}%</span>
                            </div>
                          </div>

                          <button
                            className="collapse-btn"
                            onClick={() => setExpandedInsight(null)}
                          >
                            Recolher
                            <FiChevronUp />
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
          </AnimatePresence>
        </div>

        {/* AI Performance Metrics */}
        <div className="ai-performance-section">
          <h3 className="section-title">Performance da IA</h3>
          <div className="performance-grid">
            <div className="performance-card">
              <div className="perf-icon">
                <FiTarget />
              </div>
              <div className="perf-content">
                <span className="perf-value">94%</span>
                <span className="perf-label">Precisão das Previsões</span>
              </div>
            </div>
            <div className="performance-card">
              <div className="perf-icon">
                <FiActivity />
              </div>
              <div className="perf-content">
                <span className="perf-value">127</span>
                <span className="perf-label">Insights Gerados (7d)</span>
              </div>
            </div>
            <div className="performance-card">
              <div className="perf-icon">
                <FiTrendingUp />
              </div>
              <div className="perf-content">
                <span className="perf-value">82%</span>
                <span className="perf-label">Taxa de Implementação</span>
              </div>
            </div>
            <div className="performance-card">
              <div className="perf-icon">
                <FiClock />
              </div>
              <div className="perf-content">
                <span className="perf-value">2.3s</span>
                <span className="perf-label">Tempo Médio de Análise</span>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
  };

  // Componente para Alertas
  const AlertsView = () => {
    const alerts = [
      {
        id: 1,
        type: 'critical',
        icon: FiAlertTriangle,
        title: 'Qualidade do Ar Comprometida - Ação Imediata',
        description: 'VOCs atingiram 450 ppb, excedendo limite seguro de 400 ppb. Sistema de ventilação aumentado automaticamente.',
        timestamp: '5 minutos atrás',
        status: 'active',
        autoAction: 'Ventilação aumentada em 30%'
      },
      {
        id: 2,
        type: 'warning',
        icon: FiThermometer,
        title: 'Desvio de Temperatura Detectado',
        description: 'Zona Sul apresenta temperatura 3°C acima do setpoint há 15 minutos.',
        timestamp: '15 minutos atrás',
        status: 'monitoring'
      },
      {
        id: 3,
        type: 'info',
        icon: FiInfo,
        title: 'Manutenção Programada Aproximando',
        description: 'Limpeza preventiva dos filtros agendada para próxima segunda-feira.',
        timestamp: '1 hora atrás',
        status: 'scheduled'
      }
    ];

    return (
      <div className="alerts-view-container">
        {/* Header */}
        <div className="insights-header-premium alerts-gradient">
          <div className="header-content-insights">
            <div className="header-left-insights">
              <motion.div className="header-icon-insights">
                <FiBell size={40} />
              </motion.div>
    <div>
                <h1 className="header-title-insights">Central de Alertas Inteligentes</h1>
                <p className="header-subtitle-insights">
                  Notificações em tempo real com ações automatizadas
                </p>
            </div>
          </div>
        </div>
      </div>

        {/* Alerts List */}
        <div className="alerts-list">
          {alerts.map((alert, index) => {
            const IconComponent = alert.icon;
          return (
            <motion.div
                key={alert.id}
                className={`alert-card-premium type-${alert.type}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="alert-indicator"></div>
                <div className="alert-icon-wrapper">
                  <IconComponent size={24} />
                </div>
                <div className="alert-content">
                  <h3 className="alert-title">{alert.title}</h3>
                  <p className="alert-description">{alert.description}</p>
                  {alert.autoAction && (
                    <div className="auto-action">
                      <FiZap size={14} />
                      <span>Ação Automática: {alert.autoAction}</span>
                </div>
                  )}
                  <div className="alert-footer">
                    <span className="alert-time">
                      <FiClock size={14} />
                      {alert.timestamp}
                    </span>
                    <span className={`alert-status status-${alert.status}`}>
                      {alert.status === 'active' ? 'Ativo' : 
                       alert.status === 'monitoring' ? 'Monitorando' : 'Agendado'}
                    </span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
  };

  // Componente para Padrões
  const PatternsView = () => {
    const patterns = [
      {
        id: 1,
        name: 'Padrão de Ocupação Semanal',
        type: 'occupancy',
        confidence: 96,
        description: 'IA identificou padrão consistente de ocupação com picos às 10h e 15h',
        data: [
          { day: 'Seg', morning: 75, afternoon: 85, evening: 20 },
          { day: 'Ter', morning: 78, afternoon: 88, evening: 22 },
          { day: 'Qua', morning: 80, afternoon: 90, evening: 25 },
          { day: 'Qui', morning: 77, afternoon: 87, evening: 23 },
          { day: 'Sex', morning: 70, afternoon: 75, evening: 15 }
        ]
      },
      {
        id: 2,
        name: 'Correlação Temperatura Externa vs Consumo',
        type: 'correlation',
        confidence: 89,
        description: 'Forte correlação entre temperatura externa e demanda do HVAC',
        data: [
          { temp: 20, consumption: 60 },
          { temp: 22, consumption: 65 },
          { temp: 24, consumption: 70 },
          { temp: 26, consumption: 78 },
          { temp: 28, consumption: 85 },
          { temp: 30, consumption: 92 },
          { temp: 32, consumption: 98 }
        ]
      }
    ];

    return (
      <div className="patterns-view-container">
        {/* Header */}
        <div className="insights-header-premium patterns-gradient">
          <div className="header-content-insights">
            <div className="header-left-insights">
              <motion.div className="header-icon-insights">
                <FiLayers size={40} />
              </motion.div>
    <div>
                <h1 className="header-title-insights">Padrões e Tendências Detectados</h1>
                <p className="header-subtitle-insights">
                  Análise profunda de comportamentos e correlações
                </p>
            </div>
          </div>
        </div>
      </div>

        {/* Patterns Grid */}
        <div className="patterns-grid">
          {patterns.map((pattern, index) => (
            <motion.div
              key={pattern.id}
              className="pattern-card-premium"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
            >
              <div className="pattern-header">
                <h3 className="pattern-name">{pattern.name}</h3>
                <div className="confidence-badge">
                  <FiShield />
                  <span>{pattern.confidence}% confiança</span>
                </div>
              </div>
              
              <p className="pattern-description">{pattern.description}</p>
              
              <div className="pattern-chart">
                <ResponsiveContainer width="100%" height={250}>
                  {pattern.type === 'occupancy' ? (
                    <BarChart data={pattern.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" stroke="#9ca3af" />
                      <YAxis stroke="#9ca3af" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="morning" fill="#10b981" name="Manhã" />
                      <Bar dataKey="afternoon" fill="#3b82f6" name="Tarde" />
                      <Bar dataKey="evening" fill="#f59e0b" name="Noite" />
                    </BarChart>
                  ) : (
                    <ScatterChart data={pattern.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="temp" stroke="#9ca3af" name="Temp Externa" unit="°C" />
                      <YAxis dataKey="consumption" stroke="#9ca3af" name="Consumo" unit="%" />
                      <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                      <Scatter name="Correlação" data={pattern.data} fill="#3b82f6">
                        {pattern.data.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={`rgba(59, 130, 246, ${0.3 + (entry.consumption / 100) * 0.7})`} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  )}
                </ResponsiveContainer>
              </div>
            </motion.div>
          ))}
        </div>
    </div>
  );
  };

  // Renderização baseada na subseção
  const renderContent = () => {
  switch (activeSubsection) {
      case 'ai':
        return <AISuggestionsView />;
      case 'alerts':
        return <AlertsView />;
    case 'patterns':
      return <PatternsView />;
    default:
        return <AISuggestionsView />;
    }
  };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={activeSubsection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {renderContent()}
      </motion.div>
    </AnimatePresence>
  );
};

export default Insights;

